import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useStrava } from '@/hooks/useStrava';
import { formatDateTime } from '@/utils/format';
import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/Toast';
import type { Gender } from '@/api/user';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

export function ProfileModal({ open, onClose }: ProfileModalProps) {
  const { user, logout, updateGender } = useAuth();
  const strava = useStrava(open && !!user);
  const navigate = useNavigate();
  const toast = useToast();
  const [savingGender, setSavingGender] = useState(false);

  if (!user) return null;

  const handleGenderChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as Gender;
    setSavingGender(true);
    try {
      await updateGender(next);
    } catch (err) {
      console.warn('gender save failed', err);
      toast.error('Couldn’t save gender — try again');
    } finally {
      setSavingGender(false);
    }
  };

  const fullName = user.name || user.email || '—';
  const connected = strava.status?.connected ?? false;
  const lastSyncAt = strava.status?.lastSyncAt;
  const lastSyncLabel = lastSyncAt ? `Last sync: ${formatDateTime(lastSyncAt)}` : 'Never synced';
  const syncBusy = strava.syncing || strava.importing;
  const syncMessage = strava.syncError
    ? `Sync failed: ${strava.syncError.message}`
    : strava.importing
      ? `Importing from Strava…${strava.importedCount ? ` ${strava.importedCount} added so far` : ''}`
      : strava.emitted === 0
        ? 'Already up to date.'
        : strava.emitted != null
          ? strava.importedCount
            ? `Imported ${strava.importedCount} new workout${strava.importedCount === 1 ? '' : 's'}.`
            : 'Strava is up to date.'
          : null;

  const handleLogout = async () => {
    await logout();
    onClose();
    navigate('/');
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      label="Profile"
      overlayClassName="modal-overlay"
      panelClassName="modal-content"
    >
      <div className="modal-header">
        <h2>Profile</h2>
        <button className="v5-close" onClick={onClose} aria-label="Close">
          esc <span className="x">×</span>
        </button>
      </div>

      <div className="profile-info">
        <div className="profile-avatar">
          <UserRound size={32} strokeWidth={1.5} />
        </div>
        <div className="profile-details">
          <div className="profile-row">
            <span className="profile-label">Name</span>
            <span className="profile-value">{fullName}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">Email</span>
            <span className="profile-value">{user.email}</span>
          </div>
          <div className="profile-row">
            <label className="profile-label" htmlFor="profile-gender">
              Gender
            </label>
            <select
              id="profile-gender"
              className="profile-select"
              value={user.gender ?? ''}
              onChange={(e) => void handleGenderChange(e)}
              disabled={savingGender}
            >
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      <div className="profile-strava">
        <svg
          className="profile-strava-logo"
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="#fc4c02"
          aria-hidden="true"
        >
          <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.599h4.172L10.463 0l-7 13.828h4.169" />
        </svg>
        <span className="profile-strava-label">Strava</span>
        <span className={`profile-strava-pill ${connected ? 'is-on' : 'is-off'}`}>
          {connected ? 'Connected' : 'Disconnected'}
        </span>
        {!connected && (
          <button className="profile-strava-btn" onClick={strava.connect}>
            Connect
          </button>
        )}
        {connected && (
          <>
            <button
              className="profile-strava-btn"
              onClick={() => void strava.sync()}
              disabled={syncBusy}
            >
              {strava.syncing ? 'Syncing…' : strava.importing ? 'Importing…' : 'Sync now'}
            </button>
            <button
              className="profile-strava-btn is-ghost"
              onClick={strava.disconnect}
              disabled={syncBusy}
            >
              Disconnect
            </button>
            <span className="profile-strava-msg">{lastSyncLabel}</span>
            {syncMessage && <span className="profile-strava-msg">{syncMessage}</span>}
          </>
        )}
      </div>

      <button className="btn-logout" onClick={() => void handleLogout()}>
        Log out
      </button>
    </ModalShell>
  );
}
